import { LegalPageLayout } from './LegalPageLayout';
import { legal } from './legalConfig';

function Pending({ label }: { label: string }) {
  return <span className="legal-pending" title="Pending legal confirmation">{label}</span>;
}

function EffectiveDate() {
  if (legal.effectiveDate) return <>{legal.effectiveDate}</>;
  return <Pending label="Pending approval" />;
}

export function PrivacyPolicyPage() {
  return (
    <LegalPageLayout title="Privacy Policy">
      <header className="legal-header">
        <h1>Privacy Policy</h1>
        <div className="legal-meta">
          <span className="legal-meta-badge">
            Last updated: <EffectiveDate />
          </span>
          {legal.privacyVersion && (
            <span className="legal-meta-badge">Version: {legal.privacyVersion}</span>
          )}
          {!legal.privacyVersion && (
            <span className="legal-meta-badge legal-meta-badge--pending">
              Version pending
            </span>
          )}
        </div>
      </header>

      <div className="legal-section">
        <p>
          {legal.companyName} ("Hire It Now", "we", "us", "our") is committed to protecting the
          privacy of everyone who uses or is named on our platform. This policy explains what
          personal data we collect, why we collect it, how we use it, and your rights in
          relation to it.
        </p>
        <p>
          This policy applies to the Hire It Now platform and all related services.
        </p>
        <p>
          <strong>Data controller:</strong> {legal.companyName}, {legal.companyAddress},
          Company No. {legal.companyNumber}.
          <br />
          <strong>Contact for privacy matters:</strong>{' '}
          <a href={`mailto:${legal.privacyEmail}`} className="legal-link">
            {legal.privacyEmail}
          </a>
        </p>
        <p>
          Depending on context, we act as either a data controller or a data processor. We act
          as controller for account, authentication, security, service administration, and
          platform telemetry data. We act as processor for Member-submitted business data where
          the Member determines the purposes and means of processing.
        </p>
      </div>

      <div className="legal-section">
        <h2>1. Who We Are and What We Do</h2>
        <p>
          Hire It Now is a B2B SaaS platform that helps equipment hirers, partners, and suppliers
          coordinate hire operations. The platform is used by organisations ("Members") and
          their staff and contacts ("People").
        </p>
        <p>
          For Member-submitted operational data (for example, contact records, hire requests,
          jobs, and orders), the relevant Member organisation is typically the controller and we
          process that data on their behalf.
        </p>
      </div>

      <div className="legal-section">
        <h2>2. Data We Collect and Why</h2>

        <h3>2.1 Data you give us directly (registered users)</h3>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Purpose</th>
                <th>Lawful basis</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Name, email address</td>
                <td>Account creation and identification</td>
                <td>Performance of contract (Art. 6(1)(b))</td>
              </tr>
              <tr>
                <td>Organisation name, type, slug</td>
                <td>Workspace setup and routing</td>
                <td>Performance of contract (Art. 6(1)(b))</td>
              </tr>
              <tr>
                <td>Phone number, website</td>
                <td>Optional profile information</td>
                <td>Legitimate interest (Art. 6(1)(f))</td>
              </tr>
              <tr>
                <td>Password / authentication credentials</td>
                <td>Secure access via Firebase Authentication</td>
                <td>Performance of contract (Art. 6(1)(b))</td>
              </tr>
              <tr>
                <td>Marketing consent (if given)</td>
                <td>Sending product updates and communications</td>
                <td>Consent (Art. 6(1)(a))</td>
              </tr>
              <tr>
                <td>Security metadata (e.g. IP address, user agent)</td>
                <td>Account protection, fraud prevention, and abuse monitoring</td>
                <td>Legitimate interest (Art. 6(1)(f))</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>2.2 Data collected about you indirectly (Article 14 notice)</h3>
        <p>
          If a Hire It Now Member organisation (e.g. a hiring company) adds you as a contact on
          the platform before you have registered, we hold the following data about you on
          their behalf:
        </p>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Source</th>
                <th>Purpose</th>
                <th>Lawful basis</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Name</td>
                <td>Provided by the Member</td>
                <td>Contact identification within hire operations</td>
                <td>Legitimate interest (Art. 6(1)(f)) — professional networking and hire coordination</td>
              </tr>
              <tr>
                <td>Email address</td>
                <td>Provided by the Member</td>
                <td>Sending a connection invitation or platform invite</td>
                <td>Legitimate interest (Art. 6(1)(f))</td>
              </tr>
              <tr>
                <td>Job role / phone (if provided)</td>
                <td>Provided by the Member</td>
                <td>Operational contact record</td>
                <td>Legitimate interest (Art. 6(1)(f))</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          We will notify you of this data collection when we send you an invitation email, in
          accordance with Article 14 UK GDPR. You have the right to request erasure of your
          data at any time (see section 6).
        </p>
        <p>
          The legitimate interest basis has been assessed as proportionate given that: (a) Hire
          Now is a professional business platform; (b) the purpose is operational coordination,
          not marketing; (c) disclosure is made promptly via the invite email; and (d) you can
          request erasure immediately without needing to register.
        </p>

        <h3>2.3 Data generated through platform use</h3>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Purpose</th>
                <th>Lawful basis</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Activity logs (who did what, when)</td>
                <td>Audit trail for hire operations</td>
                <td>Legitimate interest (operational integrity)</td>
              </tr>
              <tr>
                <td>Invite tokens (hashed)</td>
                <td>Single-use invite link validation</td>
                <td>Performance of contract</td>
              </tr>
              <tr>
                <td>Consent records (for processing where consent is used, such as marketing)</td>
                <td>Demonstrating lawful basis and consent history where applicable</td>
                <td>Consent and legal obligation (Art. 6(1)(a), Art. 7 UK GDPR)</td>
              </tr>
              <tr>
                <td>Notification records</td>
                <td>Delivery tracking and audit</td>
                <td>Legitimate interest</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="legal-section">
        <h2>3. Discoverability</h2>
        <p>
          Registered users may be surfaced by the platform's email existence check when another
          Member searches for a known email address. This check returns only a boolean
          (yes/no — registered), and no other information about you.
        </p>
        <p>
          You can opt out of this check at any time via the "Allow others to see that I'm on
          Hire It Now" toggle in your profile settings. When opted out, the check will return "not
          registered" for your email, preventing new connection requests.
        </p>
      </div>

      <div className="legal-section">
        <h2>4. Third Parties We Share Data With</h2>
        <p>
          We use the following third-party processors. All are bound by data processing
          agreements:
        </p>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <th>Processor</th>
                <th>Purpose</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Google Firebase (Google LLC)</td>
                <td>Authentication and identity management</td>
                <td>US (approved transfer safeguards)</td>
              </tr>
              <tr>
                <td>Microsoft Corporation (Microsoft Graph)</td>
                <td>Transactional email delivery</td>
                <td>US (approved transfer safeguards)</td>
              </tr>
              <tr>
                <td>Cloudflare Inc.</td>
                <td>Bot protection (CAPTCHA) on registration</td>
                <td>US (approved transfer safeguards)</td>
              </tr>
              <tr>
                <td>Amazon Web Services (AWS)</td>
                <td>Platform hosting and database</td>
                <td>EU (Ireland)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Where personal data is transferred outside the UK, we use appropriate safeguards under
          UK GDPR, such as the UK International Data Transfer Agreement (IDTA), the UK addendum
          to EU Standard Contractual Clauses, or adequacy regulations, as applicable.
        </p>
        <p>
          We do not sell personal data. We do not share personal data with third parties for
          their own marketing purposes.
        </p>
      </div>

      <div className="legal-section">
        <h2>5. Security Measures</h2>
        <p>
          We maintain technical and organisational measures designed to protect personal data,
          including access controls, transport encryption, appropriate security monitoring, and
          controlled backup and recovery processes.
        </p>
        <p>
          We review security controls proportionately based on risk and require relevant
          processors to apply appropriate safeguards under contractual obligations.
        </p>
      </div>

      <div className="legal-section">
        <h2>6. How Long We Keep Your Data</h2>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <th>Data type</th>
                <th>Retention period</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Active account data</td>
                <td>Duration of account + 2 years after closure</td>
              </tr>
              <tr>
                <td>Person records (soft-deleted)</td>
                <td>2 years after deletion</td>
              </tr>
              <tr>
                <td>Invite tokens</td>
                <td>30 days after the token is claimed or revoked</td>
              </tr>
              <tr>
                <td>Consent records</td>
                <td>7 years</td>
              </tr>
              <tr>
                <td>Activity logs</td>
                <td>3 years</td>
              </tr>
              <tr>
                <td>Security metadata logs</td>
                <td>7 years</td>
              </tr>
              <tr>
                <td>Database backups</td>
                <td>30 days (rolling rotation)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Where data is no longer required, it is securely deleted or anonymised.
        </p>
      </div>

      <div className="legal-section">
        <h2>7. Your Rights</h2>
        <p>Under UK GDPR, you have the following rights:</p>
        <ul>
          <li><strong>Access</strong> — request a copy of the personal data we hold about you</li>
          <li><strong>Rectification</strong> — ask us to correct inaccurate data</li>
          <li><strong>Erasure</strong> — ask us to delete your data (subject to legal retention obligations)</li>
          <li><strong>Restriction</strong> — ask us to pause processing while a dispute is resolved</li>
          <li><strong>Portability</strong> — receive your data in a machine-readable format</li>
          <li><strong>Objection</strong> — object to processing based on legitimate interest</li>
          <li><strong>Withdraw consent</strong> — for any processing based on consent (e.g. marketing emails)</li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{' '}
          <a href={`mailto:${legal.privacyEmail}`} className="legal-link">
            {legal.privacyEmail}
          </a>
          .
        </p>
        {legal.dataDeletionUrl ? (
          <p>
            For erasure requests specifically (including requests from people who were added as
            contacts before registering), use our{' '}
            <a href={legal.dataDeletionUrl} className="legal-link">
              data deletion request form
            </a>
            .
          </p>
        ) : (
          <p>
            For erasure requests, please email{' '}
            <a href={`mailto:${legal.privacyEmail}`} className="legal-link">
              {legal.privacyEmail}
            </a>{' '}
            with the subject line "Erasure Request". We will respond within one calendar month.
          </p>
        )}
        <p>
          If you are unhappy with how we handle your data, you have the right to lodge a
          complaint with the Information Commissioner's Office (ICO):{' '}
          <a href={legal.icoUrl} className="legal-link" target="_blank" rel="noopener noreferrer">
            ico.org.uk
          </a>
          , 0303 123 1113.
        </p>
      </div>

      <div className="legal-section">
        <h2>8. Changes to This Policy</h2>
        <p>
          We will notify registered users of material changes to this policy by email at least
          14 days before they take effect. The current version and effective date are shown at
          the top of this document.
        </p>
      </div>

      <div className="legal-section">
        <h2>9. Contact Us</h2>
        <div className="legal-footer">
          <p><strong>{legal.companyName}</strong></p>
          <p>{legal.companyAddress}</p>
          <p>
            <a href={`mailto:${legal.privacyEmail}`} className="legal-link">
              {legal.privacyEmail}
            </a>
          </p>
          <p>Company No. {legal.companyNumber} (England and Wales)</p>
        </div>
      </div>
    </LegalPageLayout>
  );
}
